<template>
  <div style="height: 100%">
    <div class="example-wrapper">
      <b-table
        :items="records"
        :fields="fields"
        :sort-compare="customSorting"
        :per-page="perPage"
        :current-page="currentPage"
        striped responsive="sm"
      >
        <template #cell(session)="data">
          <a href="#" @click.prevent="data.toggleDetails">
            <feather type="chevron-down" size="16px" v-if="data.detailsShowing" />
            <feather type="chevron-right" size="16px" v-else />
            {{ data.item.session.date_stamp | moment('hh:mm A, MMM Do YYYY') }}
          </a>
        </template>
        <template #cell(amount)="data">
          <span v-if="data.item.payment_type === 'Debit'">- </span>{{ data.item.amount | formatAmount(data.item.currency, 2) }}
        </template>
        <template #row-details="data">
          <hr class="mt-0" />
          <b-row>
            <b-col cols="5">Status:</b-col>
            <b-col v-if="data.item.status === 'Approved'"><strong>Completed</strong></b-col>
            <b-col v-else><strong>{{ data.item.status }}</strong></b-col>
          </b-row>
          <b-row>
            <b-col cols="5">Transaction:</b-col>
            <b-col><strong>{{ data.item.payment_type }}</strong></b-col>
          </b-row>
          <b-row v-if="formatUser(data.item)">
            <b-col cols="5" v-if="data.item.payment_type === 'Debit'">Beneficiary:</b-col>
            <b-col cols="5" v-if="data.item.payment_type === 'Credit'">Sender:</b-col>
            <b-col><strong>{{ formatUser(data.item) }}</strong></b-col>
          </b-row>
          <b-row v-if="!data.item.external || (data.item.external && data.item.reference !== data.item.external.reference)">
            <b-col cols="5">Payer Reference:</b-col>
            <b-col v-if="data.item.external">
              <strong v-if="data.item.reference !== data.item.external.reference">{{ data.item.reference }}</strong>
            </b-col>
            <b-col v-else-if="!data.transaction"><strong>{{ data.item.reference }}</strong></b-col>
          </b-row>
          <b-row v-if="data.item.external">
            <b-col cols="5">Reference:</b-col>
            <b-col><strong>{{ data.item.external.reference }}</strong></b-col>
          </b-row>
          <b-row v-if="data.item.additional && data.item.additional.purposeCode">
            <b-col cols="5">Purpose Code:</b-col>
            <b-col><strong>{{ data.item.additional.purposeCode }}</strong></b-col>
          </b-row>
          <b-row>
            <b-col cols="5" v-if="data.item.ledger === 'FX'">Conversion status:</b-col>
            <b-col cols="5" v-else-if="data.item.payment_type === 'Credit'">Funding status:</b-col>
            <b-col cols="5" v-else>Payment status:</b-col>
            <b-col><b>
              <span v-if="!data.item.external && !data.item.authorized && !data.item.authorized_by">
                Payment required authorization.<br />
              </span>
              <span v-else-if="data.item.ledger === 'Truzo Fees' && data.item.status === 'Declined'">
                Payment request declined.
              </span>
              <span v-else-if="data.item.ledger === 'Truzo Fees' && !data.item.approved">
                Truzo fees will be deducted on completion of associated payment.
              </span>
              <span v-else-if="data.item.ledger === 'Truzo Fees' && data.item.approved">
                Reference: {{ data.item.external.reference }}<br />
              </span>
              <span v-else-if="data.item.external">
                Reference: {{ data.item.external.reference }}<br />
                Status: {{ data.item.external.status.charAt(0).toUpperCase() + data.item.external.status.substr(1).replace(/_/g, ' ') }}
              </span>
              <span v-else></span>
            </b></b-col>
          </b-row>
          <b-row v-if="data.item.transaction">
            <b-col cols="5">Escrow:</b-col>
            <b-col><strong><a href="#" @click.prevent="openTransaction(data.item.transaction._id)">{{ data.item.transaction.name }}</a></strong></b-col>
          </b-row>
          <b-row>
            <b-col cols="5">Type:</b-col>
            <b-col><strong>
              <span v-if="data.item.ledger === 'Truzo Fees'">Fees</span>
              <span v-else-if="data.item.ledger === 'FX'">FX</span>
              <span v-else-if="data.item.transaction">Escrow</span>
              <span v-else>{{ data.item.payment_method }}</span>
            </strong></b-col>
          </b-row>
          <b-row>
            <b-col cols="5">Actions:</b-col>
            <b-col v-if="data.item.ledger !== 'Truzo Fees'"><strong>
              <a href="#" @click.prevent="confirm(data.item._id, 'Authorize')" v-if="canAuthorize(data)" class="mr-2 align-bottom">
                Authorize <feather type="check-square" stroke="#00c45b" size="14px"></feather>
              </a><br />
              <a href="#" @click.prevent="confirm(data.item._id, 'Decline')" v-if="canAuthorize(data)" class="align-bottom">
                Decline <feather type="x-square" stroke="orangered" size="14px"></feather>
              </a>
            </strong></b-col>
            <b-col v-else><strong>
              You must authorize the actual payment these fees are associated with.
            </strong></b-col>
          </b-row>
        </template>
      </b-table>
    </div>
    <b-pagination
      v-model="currentPage"
      :total-rows="totalRows"
      :per-page="perPage"
      pills
      align="center"
      aria-controls="statement-table"
    ></b-pagination>
    <!-- Confirmation modal -->
    <b-modal id="authorize" size="md" centered hide-header hide-footer v-if="payment.amount">
      <button type="button" aria-label="Close" class="close" @click="$bvModal.hide('authorize')">×</button>
      <h3 class="color-navy-blue mb-4">{{ action }} payment</h3>
      <b-form>
        <p>
          You're about to {{ action.toLowerCase() }} a payment of <b>{{ parseFloat(payment.amount) | formatAmount(payment.currency, 2) }}</b> to <b>{{ payment.beneficiary }}</b> created on <b>{{ payment.loaded_at | moment("Do MMM, YYYY, HH:MM") }}</b> by <b>{{ payment.loaded_by | formatUser }}</b>.<br /><br />Are you sure you want to proceed?
        </p>
        <b-form-group class="mb-0">
          <b-row>
            <b-col cols="12" md="6">
              <b-button type="button" variant="outline-primary" pill block class="mt-3" @click.prevent="doAction()">Yes</b-button>
            </b-col>
            <b-col cols="12" md="6">
              <b-button type="button" variant="custom" @click="$bvModal.hide('authorize')" pill block class="mt-3">No</b-button>
            </b-col>
          </b-row>
        </b-form-group>
      </b-form>
    </b-modal>
    <!-- End modal -->
  </div>
</template>

<script>
import { walletService } from '@/services'
import { mapActions, mapState } from 'vuex'

import moment from 'moment'

export default {
  props: {
    records: {
      type: Array,
      default: () => []
    }
  },
  data: function () {
    return {
      perPage: 15,
      currentPage: 1,
      fields: [
        {
          key: 'session',
          label: 'Date',
          sortable: true,
          formatter: (value, key, item) => {
            return moment(value.date_stamp).format('hh:mm A, MMM Do YYYY')
          },
          filterByFormatted: true
        },
        { key: 'amount', sortable: true, class: 'text-right' }
      ],
      action: 'Authorize',
      payment: {
        id: null,
        currency: null,
        amount: null,
        beneficiary: null,
        loaded_at: null,
        loaded_by: null
      }
    }
  },
  computed: {
    ...mapState({
      auth: state => state.auth.user,
      companies: state => state.company,
      compUser: state => state.companyUser
    }),
    myId () {
      if (this.auth && this.auth.user) {
        return this.auth.user.id
      } else {
        return null
      }
    },
    myRole () {
      if (this.auth && this.auth.user && this.auth.user.role) {
        return this.auth.user.role.name
      } else {
        return 'Unknown'
      }
    },
    totalRows () {
      return this.records.length
    }
  },
  methods: {
    ...mapActions('alert', {
      setSuccess: 'success',
      setError: 'error'
    }),
    doAction: async function () {
      if (this.action && this.payment.id) {
        try {
          await walletService.authorizePayment(this.payment.currency, {
            action: this.action.toLowerCase(),
            id: this.payment.id
          })

          this.$bvModal.hide('authorize')
          this.$emit('refresh', true)
          this.setSuccess('Payment request was ' + this.action.slice(0, -1).toLowerCase() + 'ed successfully.')
        } catch (error) {
          console.log(error)
          this.setError('There was an error ' + this.action.slice(0, -1).toLowerCase() + 'ing this payment. Please, try again later ...')
        }
      }
    },
    canAuthorize: function (data) {
      if (this.myRole === 'Administrators' || this.myRole === 'Authorizers') {
        if (!data.item.approved && data.item.status === 'New' && data.item.ledger !== 'Truzo Fees' && !data.item.external) {
          if (this.myId && data.item.session.created_by._id !== this.myId) {
            return true
          } else {
            return false
          }
        } else {
          return false
        }
      } else {
        return false
      }
    },
    confirm: async function (id, action) {
      if (id && action) {
        this.action = action
        const _record = this.records.find(_item => _item._id === id)
        this.payment.id = _record._id
        this.payment.currency = _record.currency
        this.payment.amount = _record.amount
        this.payment.beneficiary = _record.beneficiary
        this.payment.loaded_at = _record.session.date_stamp
        this.payment.loaded_by = _record.session.created_by
        this.$nextTick(() => {
          this.$bvModal.show('authorize')
        })
      }
    },
    customSorting: function (a, b, key, isDesc) {
      let result = 0
      if (key === 'session') {
        const day1 = new Date(a[key].date_stamp)
        const day2 = new Date(b[key].date_stamp)
        result = day1 - day2
        return Math.min(1, Math.max(-1, result))
      }
    },
    formatData (data) {
      data.forEach(record => {
        var item = {
          _id: record._id,
          payment_type: record.payment_type,
          date_stamp: moment(record.session.date_stamp).format('DD MMM, YYYY'),
          user: this.formatUser(record),
          reference: record.reference,
          ledger: record.ledger,
          payment_method: record.payment_method,
          amount: this.$options.filters.formatAmount(record.amount)
        }
        if (!this.rowData.find(_item => _item._id === record._id)) {
          this.rowData.push(item)
        }
      })
    },
    formatUser (data) {
      if (data.payment_type === 'Debit' && data.ledger === 'Truzo Fees') {
        return 'Truzo'
      } else if (data.payment_type === 'Credit' && data.sender) {
        return this.$options.filters.formatUser(data.sender)
      } else if (data.payment_type === 'Credit' && data.sender_company) {
        return data.sender_company.name
      } else if (data.payment_type === 'Debit' && data.recipient) {
        return this.$options.filters.formatUser(data.recipient)
      } else if (data.payment_type === 'Debit' && data.recipient_company) {
        return data.recipient_company.name
      } else {
        return ''
      }
    }
  },
  watch: {
    records (val) {
      this.rowData = []
      if (!val) return
      this.formatData(val)
    }
  }
}
</script>

<style lang="scss">
.example-wrapper {
  display: flex;
  flex-direction: column;
  height: calc(90vh - 16.2rem);
  background: transparent;
  overflow-x: hidden;
  overflow-y: auto;
}

#myGrid {
  flex: 1 1 0px;
  width: 100%;
}
</style>
